<script>
  import { render, gridTypes } from "../scripts/render";
  import { Plugins } from "@capacitor/core";
  import Video from "./Video.svelte";
  import { toast } from "@zerodevx/svelte-toast";
  import { SvelteToast } from "@zerodevx/svelte-toast";
  import Result from "./Result.svelte";
  import { navigate } from "svelte-navigator";

  export let grid;
  export let time;
  const gridClasses = ["matrix", "half-x", "half-y"];
  let videos = new Array();

  let expectedVideoCount = gridTypes[grid].reduce(
    (prev, row) => prev + row.length,
    0
  );

  let renderedAudio;
  let renderedVideo;

  let rendering;
  let recording;
  let latency = 0;
  let progress;

  Plugins.FFMpeg.addListener("statistic", (stat) => {
    progress = Math.min(Math.round(stat.time / 1000 / (time / 100)), 100);
    if (progress == 100) {
      progress = undefined;
    }
  });

  async function record(index) {
    if (index != 0 && !videos[0]) {
      return toast.push("Record the first grid section first.");
    }
    recording = index;
  }

  async function recordCompleted(result) {
    if (recording == 0) {
      time = result.detail.elapsed;
    }
    videos[recording] = result.detail.video;
    recording = undefined;
  }

  async function recordCancelled(result) {
    recording = undefined;
  }

  async function result() {
    rendering = true;
    renderedVideo = await render({
      videos,
      latency,
      gridType: grid,
    });
    rendering = false;
  }

  async function play() {
    rendering = true;
    renderedAudio = await render({
      videos,
      latency,
      audioOnly: true,
    });
    rendering = false;
  }

  async function showThumbnail(event) {
    const target = event.currentTarget;
    target.currentTime = 1;
    await target.play();
    setTimeout(() => target.pause(), 100);
  }

</script>

{#if recording != undefined}
  <Video
    {time}
    playbackVideo={videos.length > 0 && recording != 0
      ? URL.createObjectURL(videos[0])
      : undefined}
    on:finished={recordCompleted}
    on:canceled={recordCancelled}
  />
{:else}
  <main id="main">
    <h4>
      Record {#if progress}
        %{progress}
      {/if}

      <ion-button
        size="small"
        class="back"
        color="dark"
        on:click={() => navigate("/time/" + grid)}
      >
        <ion-icon name="arrow-back-outline" /> Back
      </ion-button>
    </h4>

    {#if progress}
      <ion-progress-bar color="danger" value={progress / 100} />
    {/if}

    {#if renderedVideo || renderedAudio}
      <Result {renderedAudio} {renderedVideo} />
    {:else}
      <div class="grid" type={gridClasses[grid]}>
        {#each gridTypes[grid] as row}
          {#each row as col}
            <div on:click={() => record(col)}>
              {#if videos[col]}
                <!-- svelte-ignore a11y-media-has-caption -->
                <video
                  src={URL.createObjectURL(videos[col])}
                  on:loadedmetadata={showThumbnail}
                  muted
                  nocontrol
                  preload="auto"
                />
              {:else}
                <ion-icon name="add-circle-outline" />
              {/if}
            </div>
          {/each}
        {/each}
      </div>
    {/if}

    <section>
      <div>
        <div class="latency-info">
          <div>
            <h6 class="small-text">Bluetooth Headset</h6>
            <small class="small-text">Estimated: 550ms</small>
          </div>
          <div>
            <h6>Latency</h6>

            <input bind:value={latency} type="number" min="1" max="5000" />
          </div>
          <div>
            <h6 class="small-text">Wired Headset</h6>
            <small class="small-text">Estimated: 300ms </small>
          </div>
        </div>

        <small>
          This section will be activated when all videos are recorded.
        </small>
      </div>
    </section>

    <section>
      <ion-button
        disabled={videos.length != expectedVideoCount || recording || rendering}
        color="warning"
        shape="round"
        on:click={play}
      >
        Pre listen
      </ion-button>

      <ion-button
        disabled={videos.length != expectedVideoCount || recording || rendering}
        color="success"
        shape="round"
        on:click={result}
      >
        Result
      </ion-button>
    </section>
  </main>

  <SvelteToast
    options={{ reversed: true, intro: { y: 192 }, duration: 3000 }}
  />
{/if}

<style>
  main {
    padding: 5px;
    display: flex;
    justify-content: space-between;
    flex-flow: column;
    gap: 5px;
  }

  main h4::before {
    content: "";
    letter-spacing: 1px;
    background: var(--ion-color-danger);
    color: var(--ion-text-color);
    display: inline-block;
    width: 30px;
    height: 30px;
    font-size: 12px;
    vertical-align: middle;
    text-align: center;
    line-height: 30px;
    border-radius: 50%;
    margin-right: 15px;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3px;
  }
  .grid div {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 4px;
    overflow: hidden;
  }
  .grid div::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: var(--ion-color-step-200);
    opacity: var(--grid-opacity);
  }
  .grid div video {
    width: 100%;
  }
  .grid div ion-icon {
    color: var(--ion-text-color);
    font-size: 40px;
  }

  small {
    font-size: 11px;
    color: #5a5b5d;
    text-align: center;
    margin: 10px 0;
  }

  h6 {
    text-align: center;
  }

  section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
  }
  section > ion-button {
    width: 100%;
  }

  section input {
    text-align: center;
    font-size: 2rem;
    width: 90px;
    border: none;
    background: none;
    outline: none;
    color: var(--ion-color-light-tint);
    padding-bottom: 5px;
    border-bottom: 2px solid;
    border-bottom-color: var(--ion-color-light-tint);
  }

  section ion-button {
    text-transform: none;
  }

  div[type="half-x"] {
    grid-template-columns: 1fr 1fr;
  }

  div[type="half-y"] {
    align-self: center;
    grid-template-columns: 1fr;
    width: 50%;
  }

  small {
    display: block;
  }

  section > div {
    text-align: center;
    width: 100%;
  }

  .latency-info {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  .small-text {
    font-size: 9px;
  }
  h4 > ion-button {
    float: right;
  }

h6 {
  margin-top: unset;
}
</style>
